import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Shield, FileText, Eye, Lock, Users, Globe, Database,
  Phone, Mail, Building2, ArrowRight, AlertCircle, Clock
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const PrivacyPage = () => {
  const { t } = useTranslation();

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Privacy Policy", path: "/privacy" }
  ];

  return (
    <div className="min-h-screen bg-offwhite" data-testid="privacy-page">
      <SEO 
        titleKey="seo.privacy.title"
        descriptionKey="seo.privacy.description"
        path="/privacy"
        breadcrumbs={breadcrumbs}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-navy-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gold/20 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-gold" />
              </div>
              <span className="text-gold font-semibold uppercase tracking-wider text-sm">
                {t('legal.badge')}
              </span>
            </div>
            <h1 className="font-space text-4xl md:text-5xl font-bold text-white mb-4">
              {t('legal.privacy.title')}
            </h1>
            <p className="text-platinum-light text-lg">
              {t('legal.privacy.subtitle')}
            </p>
            <p className="text-platinum-light/70 text-sm mt-4">
              {t('legal.privacy.lastUpdated')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            
            {/* Introduction */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.privacy.sections.introduction.title')}</h2>
                </div>
                <div className="text-slate-700 space-y-4 text-sm leading-relaxed">
                  <p>{t('legal.privacy.sections.introduction.content1')}</p>
                  <p>{t('legal.privacy.sections.introduction.content2')}</p>
                </div>
              </CardContent>
            </Card>

            {/* Data Controller */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.privacy.sections.dataController.title')}</h2>
                </div>
                <div className="text-slate-700 space-y-2 text-sm">
                  <p>{t('legal.privacy.sections.dataController.content')}</p>
                  <div className="bg-slate-50 p-4 rounded-lg mt-4">
                    <p className="font-semibold text-navy-900">{t('legal.companyName')}</p>
                    <p>The Netherlands</p>
                    <p>KvK: 42041124</p>
                    <p>EORI: NL3785680245</p>
                    <p className="mt-2">
                      <a href="mailto:info@elementarycargo.com" className="text-gold hover:underline">
                        info@elementarycargo.com
                      </a>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data We Collect */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Database className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.privacy.sections.dataWeCollect.title')}</h2>
                </div>
                <p className="text-slate-700 text-sm mb-4">{t('legal.privacy.sections.dataWeCollect.intro')}</p>
                <div className="space-y-4">
                  {['contact', 'shipment', 'account', 'technical', 'financial'].map((category) => (
                    <div key={category} className="bg-slate-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-navy-900 mb-1">{t(`legal.privacy.sections.dataWeCollect.categories.${category}.title`)}</h3>
                      <p className="text-slate-600 text-sm">{t(`legal.privacy.sections.dataWeCollect.categories.${category}.content`)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* How We Use Data */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Eye className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.privacy.sections.howWeUse.title')}</h2>
                </div>
                <p className="text-slate-700 text-sm mb-4">{t('legal.privacy.sections.howWeUse.intro')}</p>
                <ul className="space-y-3 text-sm text-slate-700">
                  {['serviceDelivery', 'contractPerformance', 'communication', 'legalCompliance', 'marketing', 'websiteImprovement'].map((purpose) => (
                    <li key={purpose} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2" />
                      <span>{t(`legal.privacy.sections.howWeUse.purposes.${purpose}`)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Legal Basis */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.privacy.sections.legalBasis.title')}</h2>
                </div>
                <p className="text-slate-700 text-sm mb-4">{t('legal.privacy.sections.legalBasis.intro')}</p>
                <ul className="space-y-3 text-sm text-slate-700">
                  {['contract', 'legalObligation', 'legitimateInterest', 'consent'].map((basis) => (
                    <li key={basis} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2" />
                      <span>{t(`legal.privacy.sections.legalBasis.bases.${basis}`)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Data Sharing */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Globe className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.privacy.sections.dataSharing.title')}</h2>
                </div>
                <p className="text-slate-700 text-sm mb-4">{t('legal.privacy.sections.dataSharing.intro')}</p>
                <ul className="space-y-3 text-sm text-slate-700 mb-4">
                  {['logistics', 'customs', 'serviceProviders', 'legal'].map((recipient) => (
                    <li key={recipient} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2" />
                      <span>{t(`legal.privacy.sections.dataSharing.recipients.${recipient}`)}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-slate-700 text-sm">{t('legal.privacy.sections.dataSharing.international')}</p>
                </div>
              </CardContent>
            </Card>

            {/* Data Retention */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.privacy.sections.dataRetention.title')}</h2>
                </div>
                <p className="text-slate-700 text-sm mb-4">{t('legal.privacy.sections.dataRetention.intro')}</p>
                <ul className="space-y-2 text-sm text-slate-700">
                  {['shipment', 'account', 'marketing', 'analytics'].map((period) => (
                    <li key={period} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2" />
                      <span>{t(`legal.privacy.sections.dataRetention.periods.${period}`)}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Your Rights */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.privacy.sections.yourRights.title')}</h2>
                </div>
                <p className="text-slate-700 text-sm mb-4">{t('legal.privacy.sections.yourRights.intro')}</p>
                <div className="grid md:grid-cols-2 gap-3 mb-6">
                  {['access', 'rectification', 'erasure', 'restriction', 'portability', 'objection', 'withdrawConsent'].map((right) => (
                    <div key={right} className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-slate-700 text-sm">{t(`legal.privacy.sections.yourRights.rights.${right}`)}</p>
                    </div>
                  ))}
                </div>
                <p className="text-slate-700 text-sm mb-2">{t('legal.privacy.sections.yourRights.howToExercise')}</p>
                <p className="text-slate-700 text-sm">{t('legal.privacy.sections.yourRights.complaint')}</p>
              </CardContent>
            </Card>

            {/* Security */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Lock className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.privacy.sections.security.title')}</h2>
                </div>
                <p className="text-slate-700 text-sm mb-4">{t('legal.privacy.sections.security.intro')}</p>
                <ul className="space-y-2 text-sm text-slate-700">
                  {(t('legal.privacy.sections.security.measures', { returnObjects: true }) || []).map((measure) => (
                    <li key={`measure-${measure.slice(0, 20)}`} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2" />
                      <span>{measure}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="mb-6 border border-platinum-light bg-navy-900">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Mail className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-white">{t('legal.privacy.sections.contact.title')}</h2>
                </div>
                <p className="text-platinum-light text-sm mb-4">{t('legal.privacy.sections.contact.intro')}</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white/10 p-4 rounded-lg">
                    <p className="text-white font-semibold mb-2">{t('legal.companyName')}</p>
                    <p className="text-platinum-light text-sm">
                      <a href="mailto:info@elementarycargo.com" className="hover:text-gold">info@elementarycargo.com</a>
                    </p>
                    <p className="text-platinum-light text-sm">
                      <a href="tel:+31625381824" className="hover:text-gold">+31 62 538 1824</a>
                    </p>
                  </div>
                  <div className="bg-white/10 p-4 rounded-lg">
                    <p className="text-white font-semibold mb-2">{t('legal.privacy.sections.contact.supervisoryAuthority')}</p>
                    <p className="text-platinum-light text-sm">{t('legal.privacy.sections.contact.supervisoryAuthorityName')}</p>
                    <p className="text-platinum-light text-sm">
                      <a href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer" className="hover:text-gold">
                        www.autoriteitpersoonsgegevens.nl
                      </a>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Other Legal Documents */}
            <Card className="border border-platinum-light">
              <CardContent className="p-8">
                <h3 className="font-space text-lg font-bold text-navy-900 mb-4">Related Documents</h3>
                <div className="flex flex-wrap gap-4">
                  <Link to="/terms">
                    <Button variant="outline" className="border-2 border-navy-900/20">
                      {t('legal.viewTerms')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to="/cookies">
                    <Button variant="outline" className="border-2 border-navy-900/20">
                      {t('legal.viewCookies')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
